import { useMemo, useState } from "react";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import TableView from "../../../common/panels/TableView/TableView";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableDefaultProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
  UseITypedEntityTableProps,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import { toUppercase } from "../../../common/helperfunctions/stringFunctions";
import { CustomImportLogFilterBar } from "./CustomImportLogFilterBar";
import {
  customImportConstants,
  CustomImportLog,
  customImportLogConstants,
  CustomImportLogFilters,
  CustomImportResult,
} from "../../../api/CustomImports";
import { datasetsConstants } from "../../../api/Datasets";
import { NotSet } from "../../../common/misc/UIconstants";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { CustomImportLogsTypeRenderer } from "../CustomImportLogsDetail";
import { usePostMutation, useUnpaginateOrdered } from "../../../api/BaseEntityApi";
import { ResourceName } from "../../../main/Routing";
import { Button } from "../../../common/buttons/Button/Button";
import { showtoast } from "../../../common/overlays/Toasts/showtoast";
import { useQueryClient } from "@tanstack/react-query";
import { ReloadMetadataTemplateConfigurationsButton } from "../../CustomImportsDetail";
import { EntityTableDeleteButton } from "../../../common/entity/entityComponents/EntityTableDeleteButton";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";

export const switchCustomImportLogsDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<CustomImportLogFilters>>[] | null | undefined
): SortState<InferSortByOptions<CustomImportLogFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "TIMESTAMP":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-timestamp",
          property: sort.property,
        };

      case "TYPE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-customType",
          property: sort.property,
        };

      case "DATASET":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-datasetID",
          property: sort.property,
        };

      default:
        return {
          sortDirection: "DESC",
          headerId: "default-id",
          property: "ID",
        };
    }
  });
};

export const defaultCustomImportLogsFilters: CustomImportLogFilters = {
  sortBy: [{ property: "ID", sortDirection: "DESC" }],
};

export const useCustomImportLogsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"customImportLogs">) => {
  const defaults: ColumnsSettings<CustomImportLog> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-timestamp": {
        pos: 1,
        active: true,
        header: fieldLabels.timestamp,
        property: "timestamp",
      },
      "default-type": {
        pos: 2,
        active: true,
        header: fieldLabels.type,
        property: "type",
      },
      "default-message": {
        pos: 3,
        active: true,
        header: fieldLabels.message,
        property: "message",
      },
      "default-datasetId": {
        pos: 4,
        active: true,
        header: fieldLabels.datasetId,
        property: "datasetId",
      },
      "default-customImportId": {
        pos: 5,
        active: true,
        header: fieldLabels.customImportId,
        property: "customImportId",
      },
    }),
    [
      fieldLabels.customImportId,
      fieldLabels.datasetId,
      fieldLabels.id,
      fieldLabels.message,
      fieldLabels.timestamp,
      fieldLabels.type,
    ]
  );

  const tabStoreDefaults: TableTabsDict<CustomImportLog, CustomImportLogFilters, any> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultCustomImportLogsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    }),
    []
  );
  return { defaults, tabStoreDefaults };
};

export const useCustomImportsTableColumns = ({
  entityConstants,
  fieldLabels,
  defaults,
  filters,
  sort,
  setSort,
}: UseITypedEntityTableProps<"customImportLogs">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<CustomImportLog> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 220,
        minWidth: 100,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },

      {
        Header: fieldLabels.timestamp,
        id: "default-timestamp",
        accessor: (row) => <DateTimeRenderer date={row.timestamp} includeDate includeTime includeElapsed />,
        minWidth: 100,
        width: 250,
        align: "left",
      },
      {
        Header: fieldLabels.message,
        id: "default-message",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>{row.message}</span>
            </div>
          </div>
        ),
        minWidth: 100,
        width: 400,
        align: "left",
        ...getAsyncSortingFn("TIMESTAMP", sort, setSort),
      },
      {
        Header: fieldLabels.type,
        id: "default-type",
        accessor: (row) => CustomImportLogsTypeRenderer({ type: row.type }),
        minWidth: 100,
        width: 120,
        align: "left",
        ...getAsyncSortingFn("TYPE", sort, setSort),
      },
      {
        Header: fieldLabels.datasetId,
        id: "default-datasetId",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={datasetsConstants.icon} color={"var(--primary)"} /> {row.datasetId ?? NotSet}
              </span>
            </div>
          </div>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("DATASET", sort, setSort),
      },
      {
        Header: fieldLabels.customImportId,
        id: "default-customImportId",
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={customImportConstants.icon} color={"var(--primary)"} /> {row.customImportId ?? NotSet}
              </span>
            </div>
          </div>
        ),
        minWidth: 100,
        width: 400,
        align: "left",
      },
    ];
    return headers;
  }, [
    fieldLabels.customImportId,
    fieldLabels.datasetId,
    fieldLabels.id,
    fieldLabels.message,
    fieldLabels.timestamp,
    fieldLabels.type,
    setSort,
    sort,
  ]);

  return { columns };
};
export const CustomImportLogsTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"customImportLogs">) => {
  const { bulkDeleteMutationAsync: deleteCustomImportLogs, isLoadingBulkDeleteMutation } = entityApi;

  const { defaults, tabStoreDefaults } = useCustomImportLogsTableDefaults({ fieldLabels });

  const { resultsCount, onCountChange, onSelectionChange, selection, selectionPermissions, onSelectionPermissions } =
    useGenericVirtualizedTable<CustomImportLog>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    dispatchTabStore,
    currentTab,
    tabsLoading,
  } = useTabStore<CustomImportLog, CustomImportLogFilters, any>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchCustomImportLogsDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useCustomImportsTableColumns({
    entityConstants,
    fieldLabels,
    defaults,
    filters,
    sort,
    setSort,
  });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  const { mutateAsync: bulkApplyCustomImportMutationAsync, isLoading: isLoadingBulkApplyCustomImportMutation } =
    usePostMutation<CustomImportResult[], { datasetIds: number[] }>({
      resource: "custom_imports/datasets" as ResourceName,
    });

  const { data: selectedCustomImportLogs, isFetching } = useUnpaginateOrdered<CustomImportLog, CustomImportLogFilters>(
    customImportLogConstants.resource,
    { ids: Array.from(selection) },
    { enabled: selection.size > 0 }
  );
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState<boolean>(false);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <div>
            {toUppercase(entityConstants.entitySingular)}{" "}
            {typeof resultsCount === "number" && <span className="badge">{resultsCount}</span>}
          </div>
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <ReloadMetadataTemplateConfigurationsButton />
          <Button
            className="btn btn-soft-success"
            title={`Apply ${customImportConstants.entitySingular} to ${selection.size} selected ${datasetsConstants.entityPlural}`}
            onClick={async () => {
              await bulkApplyCustomImportMutationAsync(
                { body: { datasetIds: selectedCustomImportLogs?.map((cil) => cil.datasetId) ?? [] } },
                {
                  onSuccess: (data) => {
                    if (data.every((d) => !d.error)) {
                      showtoast(
                        "success",
                        `Successfully applied ${customImportConstants.entitySingular} to ${selection.size} selected ${datasetsConstants.entityPlural}`
                      );
                      functionRef.current?.resetSelection();
                    } else {
                      showtoast(
                        "error",
                        `Applying ${customImportConstants.entitySingular} to ${selection.size} selected ${datasetsConstants.entityPlural} failed`
                      );
                      console.error(
                        `Applying ${customImportConstants.entitySingular} to ${selection.size} selected ${datasetsConstants.entityPlural} failed`,
                        data
                      );
                    }
                    queryClient.invalidateQueries({
                      queryKey: [customImportLogConstants.resource],
                    });
                  },
                }
              ).catch(() => {});
            }}
            loading={isLoadingBulkApplyCustomImportMutation || isFetching}
            disabled={!permissions.canReadIndex || !selectedCustomImportLogs?.length}
          >
            <LucideIcon name={customImportConstants.icon} /> Apply {customImportConstants.entitySingular}
          </Button>
          <EntityTableDeleteButton
            entityConstants={entityConstants}
            permissions={permissions}
            selection={selection}
            selectionPermissions={selectionPermissions}
            onClick={() => setShowModal(true)}
            className="btn btn-soft-danger"
          />
          <AlertModal
            type="danger"
            showModal={showModal}
            setShowModal={setShowModal}
            title={`${
              selection.size === 0
                ? `Select ${entityConstants.entitySingular} to delete`
                : selection.size === 1
                  ? `Delete the selected ${entityConstants.entitySingular}`
                  : `Delete the selected ${entityConstants.entityPlural}`
            }`}
            description={`Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`}
            proceedLabel="Delete"
            onProceed={async () => {
              await deleteCustomImportLogs({
                ids: Array.from(selection),
                goBackOnSuccess: false,
                showToast: false,
                entityName: entityConstants.entityPlural,
                params: { deletePermanently: true },
              }).catch((e) => {});
              functionRef.current?.resetSelection();
              setShowModal(false);
            }}
            loading={isLoadingBulkDeleteMutation}
          />
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar defaultSize={0}>
          <CustomImportLogFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<CustomImportLog, CustomImportLogFilters> filters={filters} excludeFilters={{}} />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
            </EntityTable.Controls>
            <EntityTable.Body<CustomImportLog, CustomImportLogFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              // dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              // showPermissionColumn
              loading={tabsLoading}
              // disablePermissionQuery
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
